import { Flex, Text } from '@chakra-ui/react';
import React, {
    memo,
    useEffect,
    useLayoutEffect,
    useMemo,
    useState,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAppDispath } from 'shared';
import { PortfolioEventCardModel } from 'entities/portfolio-event';
import { CateringEventCategoryModel } from 'entities/catering-event-category';
// eslint-disable-next-line no-restricted-imports
import { Tabs } from 'shared/ui/Tabs';
import { Helmet } from 'react-helmet-async';
import { PortfolioEventsPageActions, usePortfolioEventsPageState } from '../../store';
import { PortfolioEventsPageMainEventsList } from './PortfolioEventsPageMainEventsList';
import { PortfolioEventsPageMainHeader } from './PortfolioEventsPageMainHeader';

/**
 * Функция, которая создаёт компонент лейбла для таба
 *
 * @param {string} label Текст
 * @returns {JSX.Element} Компонент
 */
const createPortfolioEventsTabsItemLabel = (
    label: string,
) => (
    <Text fontSize="1.5rem">{label.toLocaleUpperCase()}</Text>
);

/**
 * Функция, которая создаёт компонент контента в табе
 *
 * @param {PortfolioEventModel[]} portfolioEvent Список позиций кондитерской
 * @returns {JSX.Element} Компонент
 */
const createPortfolioEventsTabsContentItem = (
    portfolioEvent: PortfolioEventCardModel[],
) => (
    <PortfolioEventsPageMainEventsList events={portfolioEvent} />
);

/**
 * Функция, которая создаёт список компонентов для табов
 *
 * @param {PortfolioEventModel[]} portfolioEvents Список позиций кондитерской
 * @param {PortfolioEventCategoryTagModel[]} sweetShopArticleCategoryTags
 * Список тегов для текущей категории
 * @returns {JSX.Element} Параметр items в TabsProps
 */
const createPortfolioEventsTabsItems = (
    portfolioEvents: PortfolioEventCardModel[],
    portfolioEventArticleCategory: CateringEventCategoryModel[],
) => portfolioEventArticleCategory.map(({
    uid,
    name,
}) => ({
    key: uid,
    labelComponent: createPortfolioEventsTabsItemLabel(name),
    contentComponent: createPortfolioEventsTabsContentItem(portfolioEvents),
}));

const MemoHeader = memo(PortfolioEventsPageMainHeader);

export const PortfolioEventsPageMain = () => {
    const dispatch = useAppDispath();
    const { portfolioEventsCards, eventCategories } = usePortfolioEventsPageState();

    const [searchParams, setSearchParams] = useSearchParams();

    const [selectedTabKey, setSelectedTabKey] = useState(searchParams.get('tab') || undefined);

    useEffect(() => {
        const urlTab = searchParams.get('tab');
        if (urlTab && urlTab !== selectedTabKey) {
            setSelectedTabKey(urlTab);
        }
    }, [searchParams.get('tab')]);

    useLayoutEffect(() => {
        dispatch(PortfolioEventsPageActions.getCateringEventCategories());
    }, []);
    useEffect(() => {
        if (!selectedTabKey) {
            setSelectedTabKey(eventCategories.at(0)?.uid);
        }
    }, [eventCategories]);

    useEffect(() => {
        if (selectedTabKey) {
            dispatch(PortfolioEventsPageActions.getPortfolioEventCardsByCategoryUid({
                uid: selectedTabKey,
            }));
            if (selectedTabKey !== searchParams.get('tab')) {
                setSearchParams({
                    tab: selectedTabKey,
                });
            }
        }
    }, [selectedTabKey]);

    /**
     * Мемоизированные табы
     */
    const memoTabsItems = useMemo(() => (
        createPortfolioEventsTabsItems(
            portfolioEventsCards,
            eventCategories,
        )
    ), [portfolioEventsCards, eventCategories]);

    return (
        <Flex
            flexDirection="column"
            gap="2rem"
            padding="2rem"
            maxWidth={{
                base: '425px',
                lg: '1280px',
            }}
            background="white"
            width="100%"
            margin="0 auto"
        >
            <Helmet>
                <title>Портфолио Corso Catering</title>
                <meta name="description" content="Портфолио Corso Catering" />
                <meta property="og:title" content="Портфолио Corso Catering" />
            </Helmet>
            <MemoHeader />
            <Tabs
                value={selectedTabKey}
                onValueChange={(v) => setSelectedTabKey(v)}
                items={memoTabsItems}
            />
        </Flex>
    );
};
